import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Clock, Flag, ChevronLeft, ChevronRight, AlertTriangle,
  CheckCircle, Send, BookOpen, Sparkles, Brain, Target, Zap
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

// Motivational messages for the AI generating screen
const MOTIVATIONAL_MESSAGES = [
  { icon: "🎯", text: "Curating questions tailored to your CUET preparation..." },
  { icon: "🧠", text: "Analyzing question patterns from previous years..." },
  { icon: "⚡", text: "Generating challenging yet achievable questions..." },
  { icon: "📊", text: "Balancing difficulty levels for optimal learning..." },
  { icon: "🎓", text: "Preparing questions that top rankers practice..." },
  { icon: "💪", text: "Building your path to 99.9 percentile..." },
  { icon: "🔥", text: "Creating questions to boost your confidence..." },
  { icon: "🌟", text: "Almost ready! Your personalized mock awaits..." },
];

// AI Generating Animation Component
function AIGeneratingScreen({ subject, progress }) {
  const [messageIndex, setMessageIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % MOTIVATIONAL_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentMessage = MOTIVATIONAL_MESSAGES[messageIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        {/* Animated Brain Icon */}
        <div className="relative mb-8">
          <div className="w-32 h-32 mx-auto relative">
            {/* Outer rotating ring */}
            <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
            {/* Middle pulsing ring */}
            <div className="absolute inset-2 border-4 border-cyan-400/40 rounded-full animate-pulse"></div>
            {/* Inner rotating ring */}
            <div className="absolute inset-4 border-4 border-pink-500/30 rounded-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}></div>
            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-2xl flex items-center justify-center animate-pulse shadow-lg shadow-purple-500/50">
                <Brain className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
          
          {/* Floating particles */}
          <div className="absolute top-0 left-1/4 w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
          <div className="absolute top-1/4 right-1/4 w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-white mb-2">
          AI is Creating Your Mock
        </h1>
        <p className="text-purple-200 mb-8">
          {subject} • 50 Questions
        </p>

        {/* Progress Bar */}
        <div className="mb-8 px-8">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 via-cyan-500 to-pink-500 rounded-full transition-all duration-500 animate-pulse"
              style={{ width: `${Math.min(progress * 100, 95)}%` }}
            ></div>
          </div>
          <p className="text-white/60 text-sm mt-2">
            {progress < 0.3 ? 'Initializing...' : progress < 0.6 ? 'Generating questions...' : progress < 0.9 ? 'Finalizing...' : 'Almost ready!'}
          </p>
        </div>

        {/* Motivational Message */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/10 transition-all duration-500">
          <div className="flex items-center justify-center space-x-3">
            <span className="text-3xl">{currentMessage.icon}</span>
            <p className="text-white/90 text-lg font-medium">
              {currentMessage.text}
            </p>
          </div>
        </div>

        {/* Fun Facts */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <Sparkles className="h-5 w-5 text-amber-400 mx-auto mb-1" />
            <p className="text-white/60 text-xs">AI-Powered</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <Target className="h-5 w-5 text-green-400 mx-auto mb-1" />
            <p className="text-white/60 text-xs">CUET Pattern</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <Zap className="h-5 w-5 text-cyan-400 mx-auto mb-1" />
            <p className="text-white/60 text-xs">Personalized</p>
          </div>
        </div>

        {/* Tip */}
        <p className="text-white/40 text-sm mt-8">
          💡 Tip: Take a deep breath and get ready to ace this!
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Smart diagram renderer — decides HOW to display diagram_ascii
// ──────────────────────────────────────────────────────────────────────────

/** Returns true only when the ASCII is genuinely informative and legible */
const isUsefulDiagram = (ascii) => {
  if (!ascii || ascii.trim().length < 10) return false;
  const lines = ascii.trim().split('\n').filter(l => l.trim());
  // Only orbital-box notation like  [ ] [↑] [↑↓]  — we'll render these as real boxes below
  const orbitalOnly = /^[\s\[\]↑↓^|Δoegt2g_12\d]+$/.test(ascii.replace(/\n/g, ' '));
  if (orbitalOnly) return false;
  // Very short single line with mostly symbols
  if (lines.length === 1 && ascii.trim().length < 30) return false;
  // High ratio of box-drawing art → not readable
  const drawingChars = (ascii.match(/[|\-=+\\\/^<>~_]{1}/g) || []).length;
  const ratio = drawingChars / ascii.replace(/\s/g, '').length;
  if (ratio > 0.70 && lines.length < 4) return false;
  return true;
};

/** Detect if ASCII looks like electron orbital filling boxes */
const isOrbitalDiagram = (ascii) => {
  return /\[[\s↑↓^]*\]/.test(ascii) && ascii.trim().length < 200;
};

/** Render orbital electron boxes as actual styled React boxes */
const OrbitalBoxRenderer = ({ ascii }) => {
  const lines = ascii.trim().split('\n').filter(l => l.trim());
  return (
    <div className="space-y-3 py-2">
      {lines.map((line, li) => {
        const labelMatch = line.match(/^([a-z0-9_]+[\s]*(?:sub)?level)?(.+)/i);
        const label = labelMatch?.[1]?.trim() || '';
        const boxPart = labelMatch?.[2] || line;
        // Extract individual orbital boxes
        const boxes = [...boxPart.matchAll(/\[([\s↑↓^]*)\]/g)];
        const extraLabel = boxPart.replace(/\[[\s↑↓^]*\]/g, '').replace(/[|]/g, '').trim();
        if (boxes.length === 0) {
          return <p key={li} className="text-sm text-gray-600 font-mono">{line}</p>;
        }
        return (
          <div key={li} className="flex items-center gap-3 flex-wrap">
            {label && <span className="text-sm font-mono font-semibold text-gray-600 min-w-[3rem]">{label}</span>}
            <div className="flex items-center gap-1">
              {boxes.map((box, bi) => {
                const content = box[1];
                const hasUp   = content.includes('↑') || content.includes('^');
                const hasDown = content.includes('↓') || content.includes('v');
                return (
                  <div key={bi} className="w-9 h-10 border-2 border-gray-500 bg-white flex flex-col items-center justify-center rounded-sm">
                    {hasUp   && <span className="text-[13px] leading-none text-gray-800 font-bold">↑</span>}
                    {hasDown && <span className="text-[13px] leading-none text-gray-800 font-bold">↓</span>}
                    {!hasUp && !hasDown && <span className="w-2 h-0.5 bg-transparent" />}
                  </div>
                );
              })}
            </div>
            {extraLabel && (
              <span className="text-sm font-mono text-blue-700 font-semibold ml-1">{extraLabel}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Master diagram renderer — chooses best display strategy */
const DiagramSection = ({ ascii, imageUrl }) => {
  const backendUrl = BACKEND_URL;
  const [imgFailed, setImgFailed] = React.useState(false);

  // Reset failed state when imageUrl changes (navigating to new question)
  React.useEffect(() => { setImgFailed(false); }, [imageUrl]);

  // If a real generated image exists and hasn't failed, show it
  if (imageUrl && !imgFailed) {
    const src = imageUrl.startsWith('http') ? imageUrl : `${backendUrl}${imageUrl}`;
    return (
      <div className="mb-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference Diagram</span>
        </div>
        <div className="p-4 flex justify-center">
          <img
            src={src}
            alt="Question diagram"
            className="max-w-full max-h-72 object-contain rounded"
            style={{ imageRendering: 'crisp-edges' }}
            onError={() => setImgFailed(true)}
          />
        </div>
      </div>
    );
  }

  if (!ascii || !ascii.trim()) return null;

  // Render orbital boxes as styled React components
  if (isOrbitalDiagram(ascii)) {
    return (
      <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50/40 overflow-x-auto">
        <div className="px-4 py-2 border-b border-indigo-100 flex items-center gap-2">
          <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Electron Configuration</span>
        </div>
        <div className="p-5">
          <OrbitalBoxRenderer ascii={ascii} />
        </div>
      </div>
    );
  }

  // Only show ASCII if it's genuinely useful
  if (!isUsefulDiagram(ascii)) return null;

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 overflow-x-auto">
      <div className="px-4 py-2 border-b border-gray-200">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference Diagram</span>
      </div>
      <div className="p-5">
        <pre
          className="text-sm text-gray-800 leading-relaxed max-w-full overflow-x-auto"
          style={{ fontFamily: "'Courier New', Courier, monospace", whiteSpace: 'pre', tabSize: 4 }}
        >
          {ascii}
        </pre>
      </div>
    </div>
  );
};
const cleanQuestionText = (text, hasDiagram = false) => {
  if (!text) return text;

  let cleaned = text;

  if (hasDiagram) {
    cleaned = cleaned.replace(/(\[\s*[\^↑↓\s]*\]\s*){2,}/g, '');
    cleaned = cleaned.replace(/([A-Z][a-z]?\s+[\/\\]\s+){2,}/g, '');
  }

  const lines = cleaned.split('\n');
  const filtered = lines.filter(line => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    const alphanumCount = (trimmed.match(/[a-zA-Z0-9α-ωΑ-Ωµ√π∫∑∏₀-₉⁰-⁹]/g) || []).length;
    if (alphanumCount > 2) {
      // Drop lines with lots of drawing chars when a diagram already exists
      if (hasDiagram) {
        const drawingRatio = (trimmed.match(/[|\-=+\\\/^<>~_]/g) || []).length / Math.max(trimmed.length, 1);
        if (drawingRatio > 0.35) return false;
      }
      return true;
    }
    if (/[=≠≤≥<>±×÷∝∞]/.test(trimmed) && trimmed.length < 20) return true;
    const nonArtChars = trimmed.replace(/[|\-=+_*~.\/\\^<>\s]/g, '');
    return nonArtChars.length > 2;
  });

  // Strip long inline ASCII art segments (e.g. energy diagrams on one line)
  const joined = filtered.join('\n');
  const inlineStripped = joined
    .replace(/[A-Za-z\s]*\^?[|/\\]+[\s/\\|^<>=\-+.]{8,}[→\-+|/\\]*\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return inlineStripped;
};

// Parse and render match-the-following questions as a structured two-column table
const renderMatchTheFollowing = (text) => {
  // Find all positions of "Column I" (not II) and "Column II" (not III) — case insensitive
  const lower = text.toLowerCase();
  const col1Positions = [];
  const col2Positions = [];

  for (let i = 0; i <= lower.length - 8; i++) {
    const slice = lower.slice(i, i + 12);
    if (/^(column|list)\s+i(?!i)/i.test(slice))  col1Positions.push(i);
    if (/^(column|list)\s+ii(?!i)/i.test(slice)) col2Positions.push(i);
  }

  if (!col1Positions.length || !col2Positions.length) return null;

  // Use last Column II as section header; use last Column I before it as section header
  const col2Pos = col2Positions[col2Positions.length - 1];
  const validCol1 = col1Positions.filter(p => p < col2Pos);
  if (!validCol1.length) return null;
  const col1Pos = validCol1[validCol1.length - 1];

  if (col1Pos >= col2Pos) return null;

  const preamble = text.slice(0, col1Pos).trim().replace(/:$/, '');
  const col1Block = text.slice(col1Pos, col2Pos).trim();
  const col2Block = text.slice(col2Pos).trim();

  const col1Regex = /^(column|list)\s+i(?!i)\s*/i;
  const col2Regex = /^(column|list)\s+ii(?!i)\s*/i;

  const col1Stripped = col1Block.replace(col1Regex, '').replace(/^\s*[:–\-\s]+/, '').trim();
  const col2Stripped = col2Block.replace(col2Regex, '').replace(/^\s*[:–\-\s]+/, '').trim();

  // Parse items from a block — handles "(P) text", "P. text", "P) text", newline or inline
  const parseItems = (stripped) => {
    const parts = stripped
      .split(/(?=\([P-S]\)|\([1-4]\)|(?:^|[\s:])([P-S]|[1-4])[.)]\s)/m)
      .map(s => s.trim()).filter(s => s.length > 1);
    if (parts.length >= 2) return parts;
    // Fallback: split on newlines
    return stripped.split('\n').map(s => s.trim()).filter(Boolean);
  };

  let col1Items = [];
  let col2Items = [];

  // Interleaved case: items like "(P) text (1) text (Q) text (2) text"
  // Detected when col1Block has very little content (nearly empty after stripping header)
  if (col1Stripped.length < 5) {
    const letterMatches = [...col2Stripped.matchAll(/\(([P-S])\)\s*(.*?)(?=\s*\([P-S]\)|\s*\([1-4]\)|$)/gs)];
    const numMatches   = [...col2Stripped.matchAll(/\(([1-4])\)\s*(.*?)(?=\s*\([P-S]\)|\s*\([1-4]\)|$)/gs)];
    col1Items = letterMatches.map(m => `(${m[1]}) ${m[2].replace(/\s+/g, ' ').trim()}`);
    col2Items = numMatches.map(m   => `(${m[1]}) ${m[2].replace(/\s+/g, ' ').trim()}`);
  } else {
    col1Items = parseItems(col1Stripped);
    col2Items = parseItems(col2Stripped);
  }

  if (!col1Items.length && !col2Items.length) return null;

  const renderItem = (item) => {
    const markerMatch = item.match(/^\(?([A-Z1-9])\)?[.)]\s*/);
    const label = markerMatch ? markerMatch[1] : '';
    const body = markerMatch ? item.slice(markerMatch[0].length).trim() : item;
    return { label, body };
  };

  // Attempt to get a descriptive header from text in parentheses near the column heading
  // But NEVER use another column heading (Column I/II) as a header label
  const getHeader = (block, fallback) => {
    const m = block.match(/\(([A-Za-z][A-Za-z /]+?)\)/);
    if (m && m[1].length < 35 && !/column|list/i.test(m[1])) {
      return m[1];
    }
    return fallback;
  };

  return (
    <div className="space-y-4">
      {preamble && (
        <p className="text-[17px] text-gray-900 leading-relaxed font-medium">{preamble}</p>
      )}
      <div className="grid grid-cols-2 gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div>
          <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-3">
            {getHeader(col1Block, 'Column I')}
          </p>
          <div className="space-y-2">
            {col1Items.map((item, i) => {
              const { label, body } = renderItem(item);
              return (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-800">
                  {label && <span className="font-bold text-blue-600 flex-shrink-0 w-5">{label}.</span>}
                  <span className="leading-snug">{body}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold text-green-600 uppercase tracking-wide mb-3">
            {getHeader(col2Block, 'Column II')}
          </p>
          <div className="space-y-2">
            {col2Items.map((item, i) => {
              const { label, body } = renderItem(item);
              return (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-800">
                  {label && <span className="font-bold text-green-600 flex-shrink-0 w-5">{label}.</span>}
                  <span className="leading-snug">{body}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Detect and style formula/equation lines differently
const renderTextWithFormulas = (text) => {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length <= 1) {
    return <p className="text-[17px] text-gray-900 leading-relaxed">{text}</p>;
  }
  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        // Detect formula lines: contain math symbols and are short/no verbs
        const isFormula = /[=≠≤≥±×÷∝∞∫√²³⁰¹⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]/.test(trimmed) &&
          trimmed.length < 80 &&
          !/\b(is|are|was|were|the|and|for|that|this|with|from|have|given)\b/i.test(trimmed);
        if (isFormula) {
          return (
            <div key={i} className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono text-[15px] text-slate-800 overflow-x-auto">
              {trimmed}
            </div>
          );
        }
        return <p key={i} className="text-[17px] text-gray-900 leading-relaxed">{trimmed}</p>;
      })}
    </div>
  );
};

// Render markdown-style tables (| col | col | format) as styled HTML tables
const renderMarkdownTable = (text) => {
  const lines = text.trim().split('\n');
  // Detect table: has lines with | and at least one separator row with |---|
  const hasPipes     = lines.some(l => l.includes('|') && l.trim().startsWith('|'));
  const hasSeparator = lines.some(l => /^\|[\s\-:|]+\|/.test(l.trim()));
  if (!hasPipes || !hasSeparator) return null;

  // Split into pre-table text, table rows, and post-table text
  const tableLines = [];
  const preLines   = [];
  const postLines  = [];
  let inTable = false;
  for (const line of lines) {
    const t = line.trim();
    if (t.startsWith('|') && t.endsWith('|')) {
      inTable = true;
      tableLines.push(t);
    } else if (inTable) {
      postLines.push(line);
    } else {
      preLines.push(line);
    }
  }

  if (tableLines.length < 2) return null;

  const parseRow = (row) =>
    row.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);

  const isSep = (row) => /^[\|\s\-:]+$/.test(row);
  const headers = parseRow(tableLines[0]);
  const dataRows = tableLines.slice(1).filter(l => !isSep(l)).map(parseRow);

  return (
    <div>
      {preLines.filter(l => l.trim()).map((l, i) => (
        <p key={i} className="text-[17px] text-gray-900 leading-relaxed mb-2">{l}</p>
      ))}
      <div className="overflow-x-auto my-4 rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-blue-50">
              {headers.map((h, i) => (
                <th key={i} className="px-4 py-2 text-left font-semibold text-blue-800 border-b border-blue-200 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, ci) => (
                  <td key={ci} className="px-4 py-2 text-gray-800 border-b border-gray-100">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {postLines.filter(l => l.trim()).map((l, i) => (
        <p key={i} className="text-[17px] text-gray-900 leading-relaxed mt-2">{l}</p>
      ))}
    </div>
  );
};

// Main question content renderer
const renderQuestionContent = (question) => {
  if (!question) return null;
  const raw = question.question_text || '';
  const cleaned = cleanQuestionText(raw, question.has_diagram);

  // Check for markdown table pattern first
  if (cleaned.includes('|') && cleaned.includes('---')) {
    const tableResult = renderMarkdownTable(cleaned);
    if (tableResult) return tableResult;
  }

  // Check for match-the-following pattern
  const isMatchType = /\bcolumn\s+i\b|\blist\s+i\b/i.test(cleaned);
  if (isMatchType) {
    const matchJSX = renderMatchTheFollowing(cleaned);
    if (matchJSX) return matchJSX;
  }

  // Check for Assertion-Reason format
  const hasAssertion = /assertion\s*\(?[Aa]\)?/i.test(cleaned);
  const hasReason = /reason\s*\(?[Rr]\)?/i.test(cleaned);
  if (hasAssertion && hasReason) {
    // Split into Assertion and Reason blocks
    const assertMatch = cleaned.match(/assertion\s*\(?[Aa]\)?[:\s]*([\s\S]+?)(?=reason\s*\(?[Rr]\)?)/i);
    const reasonMatch = cleaned.match(/reason\s*\(?[Rr]\)?[:\s]*([\s\S]+?)$/i);
    const preambleMatch = cleaned.match(/^([\s\S]+?)(?=assertion\s*\(?[Aa]\)?)/i);
    const preamble = preambleMatch ? preambleMatch[1].trim() : '';
    const assertionText = assertMatch ? assertMatch[1].trim() : '';
    const reasonText = reasonMatch ? reasonMatch[1].trim() : '';
    return (
      <div className="space-y-3">
        {preamble && (
          <p className="text-[17px] text-gray-900 leading-relaxed font-medium">{preamble}</p>
        )}
        {assertionText && (
          <div className="p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-xl">
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-1">Assertion (A)</p>
            <p className="text-[15px] text-gray-800 leading-relaxed">{assertionText}</p>
          </div>
        )}
        {reasonText && (
          <div className="p-3 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">Reason (R)</p>
            <p className="text-[15px] text-gray-800 leading-relaxed">{reasonText}</p>
          </div>
        )}
      </div>
    );
  }

  return renderTextWithFormulas(cleaned);
};

export default function MockTestPage() {
  const { mockId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // Test state
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [mockSubject, setMockSubject] = useState('');
  const [testData, setTestData] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(3600);
  const [questionTimes, setQuestionTimes] = useState({});
  const questionStartTime = useRef(Date.now());
  const [startError, setStartError] = useState(null); // replaces browser alert

  // Dialog states
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Start the test
  useEffect(() => {
    startTest();
  }, [mockId]);

  // Timer
  useEffect(() => {
    if (!testData) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleAutoSubmit();
          return 0;
        }
        if (prev === 300 && !showTimeWarning) {
          setShowTimeWarning(true);
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [testData]);

  // Track time per question
  useEffect(() => {
    questionStartTime.current = Date.now();
    return () => {
      const timeSpent = Math.floor((Date.now() - questionStartTime.current) / 1000);
      const qId = questions[currentQuestionIndex]?.id;
      if (qId) {
        setQuestionTimes(prev => ({
          ...prev,
          [qId]: (prev[qId] || 0) + timeSpent
        }));
      }
    };
  }, [currentQuestionIndex, questions]);

  const startTest = async () => {
    const minLoadTime = 8000; // Always show 8s animation for consistent UX
    const startTime = Date.now();

    // Start progress animation
    setLoadingProgress(0);
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const target = Math.min(elapsed / minLoadTime, 0.92);
      setLoadingProgress(prev => {
        if (prev >= 0.95) return prev;
        return prev + (target - prev) * 0.15;
      });
    }, 300);

    try {
      // First, get mock info to show the subject
      try {
        const mockInfoResponse = await axios.get(`${API}/mocks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (mockInfoResponse.data?.success) {
          const mockInfo = mockInfoResponse.data.data.mocks?.find(m => m.id === mockId);
          if (mockInfo) {
            setMockSubject(mockInfo.subject);
          }
        }
      } catch (e) {
        // Ignore error, just use default subject
      }

      const maxAttempts = 45; // 90 seconds max — enough for AI generation
      let attempt = 0;
      let responseData = null;

      while (attempt < maxAttempts) {
        const response = await axios.post(
          `${API}/mocks/start`,
          { mock_test_id: mockId },
          {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: () => true
          }
        );

        if (response.status === 202) {
          setLoading(true);
          attempt += 1;
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }

        if (response.data?.success) {
          responseData = response.data.data;
          break;
        }

        // Non-202, non-success → stop polling immediately
        const errorMsg = response.data?.detail || response.data?.message || 'Failed to start test';
        throw new Error(errorMsg);
      }

      if (!responseData) {
        throw new Error('Mock generation is taking longer than expected. Please go back and try again in 30 seconds.');
      }

      // Always wait for the full 8s animation to complete
      const elapsed = Date.now() - startTime;
      if (elapsed < minLoadTime) {
        await new Promise(resolve => setTimeout(resolve, minLoadTime - elapsed));
      }

      // Set progress to 100% and brief pause
      setLoadingProgress(1);
      await new Promise(resolve => setTimeout(resolve, 600));

      setTestData(responseData.mock_test);
      setAttemptId(responseData.attempt_id);
      setQuestions(responseData.questions);
      setTimeRemaining(responseData.time_remaining_seconds);

      const initialAnswers = {};
      responseData.questions.forEach(q => {
        initialAnswers[q.id] = null;
      });
      setAnswers(initialAnswers);
      clearInterval(progressInterval);
      return;
    } catch (error) {
      console.error('Error starting test:', error);
      const errMsg = error.response?.data?.detail || error.message || 'Failed to start test';
      setStartError(errMsg);
      setLoading(false);
    } finally {
      clearInterval(progressInterval);
      setLoading(false);
    }
  };

  const selectAnswer = (questionId, optionId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));
  };

  const toggleFlag = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const goToQuestion = (index) => {
    // Record time for current question before switching
    const timeSpent = Math.floor((Date.now() - questionStartTime.current) / 1000);
    const currentQId = questions[currentQuestionIndex]?.id;
    if (currentQId) {
      setQuestionTimes(prev => ({
        ...prev,
        [currentQId]: (prev[currentQId] || 0) + timeSpent
      }));
    }
    
    setCurrentQuestionIndex(index);
  };

  const handleAutoSubmit = () => {
    handleSubmit();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setShowSubmitDialog(false);

    // Record final time for current question
    const timeSpent = Math.floor((Date.now() - questionStartTime.current) / 1000);
    const currentQId = questions[currentQuestionIndex]?.id;
    const finalQuestionTimes = {
      ...questionTimes,
      [currentQId]: (questionTimes[currentQId] || 0) + timeSpent
    };

    // Prepare submission
    const submissionAnswers = questions.map(q => ({
      question_id: q.id,
      selected_option_id: answers[q.id] || null,
      time_spent_seconds: finalQuestionTimes[q.id] || 0
    }));

    try {
      const response = await axios.post(
        `${API}/mocks/submit`,
        {
          attempt_id: attemptId,
          answers: submissionAnswers
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        navigate(`/results/${attemptId}`, { 
          state: { result: response.data.data } 
        });
      } else {
        setStartError(response.data?.detail || response.data?.message || 'Submission failed. Please try again.');
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to submit test. Please try again.';
      setStartError(msg);
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs > 0 ? hrs + ':' : ''}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (index) => {
    const qId = questions[index]?.id;
    if (flaggedQuestions.has(qId)) return 'flagged';
    if (answers[qId]) return 'answered';
    return 'unanswered';
  };

  const getStats = () => {
    let answered = 0, flagged = 0, unanswered = 0;
    questions.forEach((q, idx) => {
      if (answers[q.id]) answered++;
      else unanswered++;
      if (flaggedQuestions.has(q.id)) flagged++;
    });
    return { answered, flagged, unanswered };
  };

  if (loading) {
    return (
      <AIGeneratingScreen 
        subject={mockSubject || 'Your Subject'} 
        progress={loadingProgress} 
      />
    );
  }

  // Show inline error instead of browser alert
  if (startError) {
    // Parse structured monthly limit error: "MONTHLY_LIMIT_REACHED|Chemistry|pro|8"
    const limitMatch = startError.match(/^MONTHLY_LIMIT_REACHED\|(.+)\|(\w+)\|(\d+)$/);
    const isMonthlyLimit = !!limitMatch;
    const isUpgradeError = startError.includes('free mock') || startError.includes('limit') || isMonthlyLimit;

    let errorTitle = "Unable to Start Test";
    let errorMessage = startError;
    let showUpgrade = false;
    let upgradeLabel = "View Plans";

    if (limitMatch) {
      const [, subject, plan, limit] = limitMatch;
      const planLabel = plan === 'pro' ? 'Pro' : 'Premium';
      errorTitle = `${subject} Mocks Exhausted`;
      errorMessage = `You've used all ${limit} mocks for ${subject} this month on your ${planLabel} plan.`;
      if (plan === 'pro') {
        showUpgrade = true;
        upgradeLabel = "Upgrade to Premium";
        errorMessage += ` Upgrade to Premium for 10 mocks per subject, or write to us at info@CrackCUET.com if you need more access.`;
      } else {
        errorMessage += ` Your monthly limit resets at the start of next month. Write to us at info@CrackCUET.com if you need additional access.`;
      }
    } else if (startError.includes('free mock')) {
      errorTitle = "Free Mock Used";
      errorMessage = startError;
      showUpgrade = true;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{errorTitle}</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
            {isUpgradeError && (
              <Button onClick={() => navigate('/upgrade')} className="bg-blue-600 hover:bg-blue-700 text-white">
                {upgradeLabel}
              </Button>
            )}
            {isMonthlyLimit && (
              <a href="mailto:info@crackcuet.com">
                <Button variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
                  Contact Support
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }


  if (!testData || questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-700">
          <p>Failed to load test. Please try again.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const stats = getStats();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-3 px-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-xl font-bold text-gray-900">{testData.title}</span>
            <Badge className="bg-brand text-white border-0">
              {testData.subject}
            </Badge>
          </div>

          {/* Timer */}
          <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
            timeRemaining <= 300 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
          }`}>
            <Clock className="h-5 w-5" />
            <span className="font-mono text-lg font-bold">{formatTime(timeRemaining)}</span>
          </div>

          {/* Submit Button */}
          <Button
            onClick={() => setShowSubmitDialog(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="submit-test-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Submit Test
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Question Panel */}
        <div className="flex-1 p-6">
          <Card className="bg-white border-gray-200 shadow-sm h-full">
            <CardContent className="p-6">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Badge className="bg-cta text-white border-0">
                    Q{currentQuestionIndex + 1}/{questions.length}
                  </Badge>
                  <span className="text-sm text-gray-500">
                    +{currentQuestion.marks} marks / -{currentQuestion.negative_marks} negative
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleFlag(currentQuestion.id)}
                  className={`${
                    flaggedQuestions.has(currentQuestion.id)
                      ? 'bg-amber-50 text-amber-600 border-amber-300'
                      : 'text-gray-500 border-gray-300 hover:text-gray-700'
                  }`}
                  data-testid="flag-question-btn"
                >
                  <Flag className="h-4 w-4 mr-2" />
                  {flaggedQuestions.has(currentQuestion.id) ? 'Flagged' : 'Flag for Review'}
                </Button>
              </div>

              {/* Passage (for comprehension questions) */}
              {currentQuestion.passage && (
                <div className="mb-6 p-5 rounded-xl border border-blue-200 bg-blue-50/50 overflow-x-auto" data-testid="passage-block">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-700">Reading Passage</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-[15px] whitespace-pre-line break-words">
                    {currentQuestion.passage}
                  </p>
                </div>
              )}

              {/* Diagram — smart renderer (real image / orbital boxes / ASCII / hidden if not useful) */}
              <DiagramSection ascii={currentQuestion.diagram_ascii} imageUrl={currentQuestion.diagram_image_url} />

              {/* Question Text */}
              <div className="mb-8 space-y-3">
                {renderQuestionContent(currentQuestion)}
              </div>

              {/* Options */}
              <div className="space-y-3">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={option.id}
                    onClick={() => selectAnswer(currentQuestion.id, option.id)}
                    data-testid={`option-${String.fromCharCode(65 + idx)}`}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      answers[currentQuestion.id] === option.id
                        ? 'border-blue-500 bg-blue-50 text-gray-900'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        answers[currentQuestion.id] === option.id
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="flex-1 break-words">{option.text}</span>
                      {answers[currentQuestion.id] === option.id && (
                        <CheckCircle className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                  className="border-gray-300 text-gray-600 hover:text-gray-900"
                >
                  <ChevronLeft className="h-5 w-5 mr-2" />
                  Previous
                </Button>

                {answers[currentQuestion.id] && (
                  <Button
                    variant="ghost"
                    onClick={() => selectAnswer(currentQuestion.id, null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Clear Response
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={currentQuestionIndex === questions.length - 1}
                  className="border-gray-300 text-gray-600 hover:text-gray-900"
                >
                  Next
                  <ChevronRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Question Navigator */}
        <div className="w-80 bg-white border-l border-gray-200 p-4">
          <h3 className="text-gray-900 font-semibold mb-4">Question Navigator</h3>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.answered}</p>
              <p className="text-xs text-green-600">Answered</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
              <p className="text-2xl font-bold text-gray-500">{stats.unanswered}</p>
              <p className="text-xs text-gray-500">Unanswered</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.flagged}</p>
              <p className="text-xs text-amber-600">Flagged</p>
            </div>
          </div>

          {/* Question Grid */}
          <div className="grid grid-cols-5 gap-2">
            {questions.map((q, idx) => {
              const status = getQuestionStatus(idx);
              return (
                <button
                  key={q.id}
                  onClick={() => goToQuestion(idx)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                    idx === currentQuestionIndex
                      ? 'ring-2 ring-blue-500 ring-offset-2'
                      : ''
                  } ${
                    status === 'answered'
                      ? 'bg-green-500 text-white'
                      : status === 'flagged'
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span className="text-gray-600">Answered</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-4 h-4 rounded bg-gray-100 border border-gray-300"></div>
              <span className="text-gray-600">Unanswered</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-4 h-4 rounded bg-amber-500"></div>
              <span className="text-gray-600">Flagged for Review</span>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle>Submit Test</DialogTitle>
            <DialogDescription className="text-gray-500">
              Are you sure you want to submit your test? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-2xl font-bold text-green-600">{stats.answered}</p>
                <p className="text-sm text-green-600">Answered</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-500">{stats.unanswered}</p>
                <p className="text-sm text-gray-500">Unanswered</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-2xl font-bold text-amber-600">{stats.flagged}</p>
                <p className="text-sm text-amber-600">Flagged</p>
              </div>
            </div>

            {stats.unanswered > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center space-x-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
                <span className="text-sm">You have {stats.unanswered} unanswered questions.</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSubmitDialog(false)}
              className="border-gray-300 text-gray-600"
            >
              Continue Test
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {submitting ? 'Submitting...' : 'Submit Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Warning Dialog */}
      <Dialog open={showTimeWarning} onOpenChange={setShowTimeWarning}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              <span>5 Minutes Remaining!</span>
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              You have only 5 minutes left. Please review your answers and submit the test.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowTimeWarning(false)}
              className="bg-cta hover:bg-cta-hover text-white"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
